import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePublicationDetail } from '../../../hooks/usePublicationDetail';
import { usePublications } from '../../../hooks/usePublications';
import { useTenant } from '../../../contexts/TenantContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import PublicationMedia, { LinkifiedText } from './PublicationMedia';
import LikeShareBar from './LikeShareBar';
import './PublicationDetailPage.css';

interface PublicationDetailPageProps {
  tenantId?: string;
  customerId?: string;
}

const PublicationDetailPage: React.FC<PublicationDetailPageProps> = ({
  tenantId: propTenantId,
  customerId: propCustomerId,
}) => {
  const { tenant } = useTenant();
  const { customer } = useCustomer();

  const tenantId = propTenantId || tenant?.id || '';
  const customerId = propCustomerId || customer?.id;

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { publication, loading, error } = usePublicationDetail(id!, tenantId);
  const { trackView } = usePublications({}, tenantId);
  const [viewStartTime] = useState(Date.now());

  useEffect(() => {
    if (!id) return;

    // Tracker la vue à l'ouverture
    trackView(id, { customer_id: customerId });

    // Tracker la durée à la fermeture
    return () => {
      const duration = Math.floor((Date.now() - viewStartTime) / 1000);
      trackView(id, {
        customer_id: customerId,
        view_duration_seconds: duration,
      });
    };
  }, [id, customerId, viewStartTime, trackView]);

  const handleProductClick = (productId: string) => {
    // Tracker le clic sur produit
    if (id) {
      trackView(id, {
        customer_id: customerId,
        clicked_product: true,
      });
    }

    // La BOUTIQUE, pas le tableau de bord. `/products/:id` est une route du
    // dashboard commerçant : un client y tombait sur l'écran de connexion du
    // personnel — le même défaut que la racine du site, corrigé le 27/08.
    navigate(`/shop?produit=${productId}`);
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      ARRIVAGE: '🍅',
      PROMOTION: '🎉',
      ANTI_GASPI: '🌱',
      EVENEMENT: '🎪',
      INFO: 'ℹ️',
    };
    return icons[type] || '📢';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ARRIVAGE: 'Arrivage',
      PROMOTION: 'Promotion',
      ANTI_GASPI: 'Anti-gaspi',
      EVENEMENT: 'Événement',
      INFO: 'Information',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="publication-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !publication) {
    return (
      <div className="publication-detail-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Publication introuvable</h2>
          <p>{error || 'Cette publication n\'existe pas ou n\'est plus disponible'}</p>
          <button onClick={() => navigate(-1)} className="btn-back">
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="publication-detail-page">
      {/* Header avec retour */}
      <div className="detail-header">
        <button onClick={() => navigate(-1)} className="btn-back-circle">
          ←
        </button>
        <div className="header-info">
          <span className="type-badge" style={{ background: getTypeBadgeColor(publication.type) }}>
            {getTypeIcon(publication.type)} {getTypeLabel(publication.type)}
          </span>
        </div>
        <LikeShareBar
          publicationId={publication.id}
          title={publication.title}
          likesCount={publication.likes_count ?? 0}
          liked={publication.liked}
        />
      </div>

      {/* Images carousel */}
      {publication.images && publication.images.length > 0 && (
        <div className="images-carousel">
          {publication.images.map((image) => (
            <div key={image.id} className="carousel-image">
              <img src={image.url} alt={image.caption || publication.title} />
              {image.caption && <div className="image-caption">{image.caption}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Infos marchand — seulement si l'API l'a renvoyé */}
      {publication.tenant?.name && (
        <div className="merchant-section">
          <div className="merchant-avatar-large">
            {publication.tenant.name.charAt(0).toUpperCase()}
          </div>
          <div className="merchant-details">
            <h3>{publication.tenant.name}</h3>
            {publication.tenant.city && (
              <p className="merchant-location">📍 {publication.tenant.city}</p>
            )}
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="detail-content">
        <h1 className="detail-title">{publication.title}</h1>

        <div className="detail-meta">
          <span>👁️ {publication.views_count} vues</span>
          {publication.clicks_count > 0 && (
            <span>🛒 {publication.clicks_count} clics</span>
          )}
          <span>
            {new Date(publication.published_at || publication.created_at).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
        </div>

        {/* Vidéos / podcasts : le lecteur, avant le texte */}
        {publication.videos?.map((video) => (
          <PublicationMedia key={video.id || video.url} video={video} title={publication.title} />
        ))}

        <LinkifiedText className="detail-text" text={publication.content} />
      </div>

      {/* Produits */}
      {publication.products && publication.products.length > 0 && (
        <div className="products-section">
          <h2 className="section-title">Produits concernés</h2>
          <div className="products-grid">
            {publication.products.map((item) => (
              <div
                key={item.id}
                className="product-card"
                onClick={() => handleProductClick(item.product_id)}
              >
                {/* `image_url` est le champ LEGACY, remplacé par `product_images`
                    en mars 2026 : il est vide sur tout ce qui a été photographié
                    depuis, donc aucune photo ne s'affichait ici. On lit la
                    vignette, avec repli sur l'ancien champ pour les fiches qui
                    n'ont jamais été reprises. */}
                {(() => {
                  const photo =
                    item.product.images?.find((i) => i.type !== 'LABEL')?.thumbnail_url ||
                    item.product.images?.find((i) => i.type !== 'LABEL')?.url ||
                    item.product.image_url;
                  return photo ? (
                    <div className="product-image">
                      <img src={photo} alt={item.product.name} />
                    </div>
                  ) : null;
                })()}
                <div className="product-details">
                  <h3 className="product-name">{item.product.name}</h3>

                  {item.special_offer && (
                    <div className="special-offer-badge">
                      {item.special_offer}
                    </div>
                  )}

                  {item.highlight_price && item.product.prices?.[0] && (
                    <div className="product-price">
                      {(item.product.prices[0].amount_cents / 100).toFixed(2)} €
                    </div>
                  )}

                  {/* Le bouton disait « Ajouter au panier » et n'avait AUCUN
                      code derrière — un bouton décoratif est pire qu'un bouton
                      absent. Il ouvre maintenant le produit dans la boutique, où
                      la quantité se choisit vraiment (au poids, à la pièce) :
                      réimplémenter ce choix ici en aurait fait une deuxième
                      règle de vente, à côté de celle du catalogue. */}
                  <button
                    className="btn-add-to-cart"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(item.product_id);
                    }}
                  >
                    Voir dans la boutique
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expiration */}
      {publication.expires_at && new Date(publication.expires_at) > new Date() && (
        <div className="expiration-notice">
          ⏰ Offre valable jusqu'au{' '}
          {new Date(publication.expires_at).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}
    </div>
  );
};

const getTypeBadgeColor = (type: string) => {
  const colors: Record<string, string> = {
    ARRIVAGE: '#10b981',
    PROMOTION: '#f59e0b',
    ANTI_GASPI: '#22c55e',
    EVENEMENT: '#8b5cf6',
    INFO: '#3b82f6',
  };
  return colors[type] || '#6b7280';
};



export default PublicationDetailPage;
